import { Component, inject, Input } from "@angular/core";
import { Fiat, IVSNFeeTokenCollection } from "@models";
import { Router } from "@angular/router";
import { SuccessComponent } from "../success.interface";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { SwapConfirmTrade } from "@models/swap-confirm-trade";
import { Index } from "@models/asset/index/index.model";
import { Tradable } from "@models/interfaces/tradable";
import { AccountService } from "@modules/account/services/account.service";
import { FiatService } from "@main-services/currency";
import { SegmentService } from "@main-services/segment.service";
import { FiatPrecisionOptions } from "@modules/core/pipes";
import { TradeSwapComponent } from "@modules/trade/modals/trade-swap-modal/trade-swap.component";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { AutoStakingFacade } from "@modules/staking/domain/+state/auto-staking.facade";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import { combineLatest, Observable, tap } from "rxjs";
import { getStakeableHelper } from "@helpers/functions";
import { map, take } from "rxjs/operators";
import { StakeableAvailability } from "@modules/staking/domain/entities/stakable-availability.model";
import { StakingEntryHelperService } from "@modules/staking/services/staking-entry.helper.service";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { MoSuccessVisionBannerState } from "@modules/web3/components/mo-success-vision-component/mo-success-vision-banner-state.model";
import { WEB3_FEE_BENEFFIT_FLOW, Web3VSNFeeBenefitFF } from "@modules/web3/interface/web3.benefit.ui.interface";
import { OrderFlow } from "@modules/limit-orders/interfaces/limit-order-rudderstack.model";
import { CheckoutEntryHelperService } from "@modules/checkout/services/checkout-entry-helper.service";
import { LimitOrdersTrackingFacade } from "@modules/limit-orders/services/orders-rudderstack.facade";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { RudderstackService } from "@main-services/rudderstack.service";

@Component({
	selector: "swap-success",
	templateUrl: "./swap-success.component.html",
	styleUrls: ["./../success-screen.component.scss"],
	standalone: false
})
export class SwapSuccessComponent extends SuccessComponent {
	@Input()
	public swapSuccessTrades: SwapConfirmTrade[];
	@Input()
	public visionFeeTokenCollection: IVSNFeeTokenCollection;

	private indexPipeOption: FiatPrecisionOptions = new FiatPrecisionOptions();

	protected readonly flowName: string = "Swap";
	protected readonly pathPrepend: string = "/trade/swap";

	private readonly autoStakingFacade = inject(AutoStakingFacade);
	private readonly stakingFacade = inject(StakingFacade);
	private readonly stakingEntryHelper = inject(StakingEntryHelperService);

	private launchDarklyService = inject(LaunchDarklyService);

	public showVisionWarning = false;
	public assetAvailableToStake = false;

	public visionFeeFlag$ = this.launchDarklyService
		.getFeatureConfig$<Web3VSNFeeBenefitFF>(LD_FLAG.WEB3_VSN_FEE_BENEFIT)
		.pipe(map((d) => d[WEB3_FEE_BENEFFIT_FLOW.swap]));
	public visionId$ = this.launchDarklyService
		.getFeatureConfig$<{
			pid: string;
		}>(LD_FLAG.VISION_PID)
		.pipe(map((data) => this.tradableListService.getItemByPid(data.pid)));
	protected checkoutEntryHelper = inject(CheckoutEntryHelperService);
	protected limitOrdersTrackingFacade = inject(LimitOrdersTrackingFacade);

	protected readonly profileFacade = inject(NewProfileFacade);
	public isUkUser$ = this.profileFacade.isUkResident$();

	stakingVm$ = combineLatest({
		stakeables: this.stakingFacade.getStakeables$(),
		autoStakingState: this.autoStakingFacade.getOrFetchAutoStakingState$(),
		visionPid: this.getVisionPid$()
	}).pipe(
		tap((stakingVm) => {
			const stakeableHelper = getStakeableHelper(stakingVm.stakeables, this.swapSuccessTrades[1].getCurrencyId());
			this.assetAvailableToStake = stakeableHelper.stakeable?.availability === StakeableAvailability.ENABLED;
			this.showVisionWarning =
				stakeableHelper.isStakeable &&
				!stakeableHelper.stakeable?.autoStakingEnabled &&
				stakingVm.autoStakingState.autoStakingSwitchOn;
		})
	);

	public constructor(
		protected router: Router,
		protected tradableListService: TradableListService,
		protected fiatService: FiatService,
		protected accountService: AccountService,
		protected segmentService: SegmentService,
		protected rudderstackService: RudderstackService
	) {
		super(fiatService, tradableListService, accountService, router, segmentService, rudderstackService);

		this.indexPipeOption.id = this.defaultFiat.getId();
		this.indexPipeOption.currencies = [this.defaultFiat];
		this.indexPipeOption.symbol = true;
	}

	public tradeMore(): void {
		this.close();
		setTimeout(() => {
			this.modalService.create(TradeSwapComponent).present();
		}, 300);
	}

	public startStakingFlow(assetId: string): void {
		this.close();
		this.stakingEntryHelper.openStakingFlow(assetId, TRADE_TYPE.STAKE);
	}

	public getVisionBannerState(): MoSuccessVisionBannerState {
		if (
			this.visionFeeTokenCollection?.globalOptIn &&
			!this.visionFeeTokenCollection?.visionFeeDetail?.hasEnoughFunds
		) {
			return MoSuccessVisionBannerState.OPTEDININSUFFICIENTFUNDS;
		}
		return MoSuccessVisionBannerState.VISIONFEENOTACTIVE;
	}

	public handleBannerClick(): void {
		this.close();
		const bannerState = this.getVisionBannerState();
		if (bannerState === MoSuccessVisionBannerState.OPTEDININSUFFICIENTFUNDS) {
			this.limitOrdersTrackingFacade.updateFlow(OrderFlow.VisionUpSellBanner);
			this.visionId$.pipe(take(1)).subscribe((visionTradable) => {
				this.checkoutEntryHelper.onTradableSelect(visionTradable.getId());
			});
		} else if (bannerState === MoSuccessVisionBannerState.VISIONFEENOTACTIVE) {
			// TODO: review this
			this.router.navigateByUrl("/my-account/my-vision");
		} else {
			return;
		}
	}

	private getVisionPid$(): Observable<String> {
		return this.launchDarklyService
			.getFeatureConfig$<{
				pid: string;
			}>(LD_FLAG.VISION_PID)
			.pipe(map((data) => data.pid));
	}

	public getCurrencySymbol(trade: SwapConfirmTrade): string {
		const currency = this.getTradable(trade.getCurrencyId());
		if (currency instanceof Index) {
			return this.defaultFiat.getSymbol();
		}
		currency.getSymbol();
	}

	private getWalletPath(trade: SwapConfirmTrade): string {
		let path = "";
		const currency = this.getTradable(trade.getCurrencyId());
		if (currency instanceof Index) {
			path = "/index-wallet/";
		} else {
			path = "/wallet/";
		}
		path += trade.getWalletId();
		return path;
	}

	public getTradableName(index: number): string {
		return this.getTradable(this.swapSuccessTrades[index].getCurrencyId()).getName();
	}

	public getTradableSymbol(index: number): string {
		return this.getTradable(this.swapSuccessTrades[index].getCurrencyId()).getSymbol(false, true);
	}

	public isIndexSwap(): boolean {
		return this.swapSuccessTrades.some((trade: SwapConfirmTrade) => this.isIndex(trade));
	}

	public isIndex(trade: SwapConfirmTrade): boolean {
		const currency: Tradable = this.getTradable(trade.getCurrencyId());
		return currency instanceof Index;
	}

	public getIndexSwapType(): string {
		const fromIndex: boolean = this.isIndex(this.swapSuccessTrades[0]);
		const toIndex: boolean = this.isIndex(this.swapSuccessTrades[1]);
		if (fromIndex) {
			if (toIndex) {
				return "betweenIndex";
			}
			return "fromIndex";
		}

		return "toIndex";
	}

	public getIndexPipeOption(): FiatPrecisionOptions {
		return this.indexPipeOption;
	}

	public get defaultFiat(): Fiat {
		return this.accountService.getDefaultFiat();
	}

	protected readonly MoSuccessVisionBannerState = MoSuccessVisionBannerState;
}
